import React, { useState, useEffect } from 'react';
import { ticketAPI, userAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import { FiMessageSquare, FiPlus, FiX, FiCheck, FiRefreshCw, FiAlertCircle, FiFilter } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const LineTickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    topic: '',
    comment: '',
    assigneeId: ''
  });
  
  const [commentModal, setCommentModal] = useState({ show: false, ticketId: null, comment: '' });
  const [submittingComment, setSubmittingComment] = useState(false);
  
  const [statusModal, setStatusModal] = useState({ show: false, ticketId: null, status: '' });
  const [submittingStatus, setSubmittingStatus] = useState(false);

  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchUsers();
    }
  }, [user?.role]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await ticketAPI.getTickets();
      setTickets(res.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await userAPI.getUsers({ limit: 100 });
      if (res?.data?.items) {
        setUsers(res.data.items);
      } else if (Array.isArray(res?.data)) {
        setUsers(res.data);
      }
    } catch (error) {
      console.warn('Could not load users list:', error.message);
    }
  };

  const fetchData = () => {
    fetchTickets();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.topic || !formData.assigneeId) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }
    
    setSubmitting(true);
    try {
      await ticketAPI.createTicket({
        ...formData,
        assigneeId: parseInt(formData.assigneeId)
      });
      toast.success('สร้างรายการเรียบร้อยแล้ว');
      setShowModal(false);
      setFormData({ topic: '', comment: '', assigneeId: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('ไม่สามารถสร้างรายการได้');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNotifyAgain = async (id) => {
    try {
      await ticketAPI.notifyAgain(id);
      toast.success('ส่งการแจ้งเตือนอีกครั้งแล้ว');
      fetchData();
    } catch (error) {
      console.error('Error notifying:', error);
      toast.error(error.response?.data?.message || 'ไม่สามารถส่งการแจ้งเตือนได้ (จำกัด 1 ครั้งต่อวัน)');
    }
  };

  const handleStatusChange = async (e) => {
    e.preventDefault();
    if (!statusModal.status) return;
    setSubmittingStatus(true);
    try {
      await ticketAPI.updateTicket(statusModal.ticketId, { status: statusModal.status });
      toast.success('เปลี่ยนสถานะเรียบร้อยแล้ว');
      setStatusModal({ show: false, ticketId: null, status: '' });
      fetchData();
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error('ไม่สามารถเปลี่ยนสถานะได้');
    } finally {
      setSubmittingStatus(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentModal.comment) return;
    setSubmittingComment(true);
    try {
      await ticketAPI.updateTicket(commentModal.ticketId, { comment: commentModal.comment });
      toast.success('เพิ่มความเห็นแล้ว');
      setCommentModal({ show: false, ticketId: null, comment: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('ไม่สามารถเพิ่มความเห็นได้');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'created': return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">สร้างใหม่</span>;
      case 'notify': return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">แจ้งเตือนแล้ว</span>;
      case 'pending': return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">รอดำเนินการ</span>;
      case 'closed': return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">ปิดแล้ว</span>;
      default: return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const filteredTickets = tickets.filter(t => {
    if (!dateFilter.from && !dateFilter.to) return true;
    const ticketDate = new Date(t.createdAt).setHours(0, 0, 0, 0);
    const fromDate = dateFilter.from ? new Date(dateFilter.from).setHours(0, 0, 0, 0) : null;
    const toDate = dateFilter.to ? new Date(dateFilter.to).setHours(23, 59, 59, 999) : null;
    
    if (fromDate && toDate) return ticketDate >= fromDate && ticketDate <= toDate;
    if (fromDate) return ticketDate >= fromDate;
    if (toDate) return ticketDate <= toDate;
    return true;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  if (loading) return <LoadingSpinner />;

  const canCreate = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="pb-20">
      <div className="bg-white p-4 shadow-sm mb-4 sticky top-0 z-10 border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-xl font-bold text-gray-900">Tickets (รายการแจ้ง)</h1>
            {canCreate && (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center justify-center bg-indigo-600 text-white p-2 rounded-full shadow hover:bg-indigo-700"
              >
                <SafeIcon icon={FiPlus} className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded-lg border border-gray-100 sm:max-w-md">
            <SafeIcon icon={FiFilter} className="text-gray-400 flex-shrink-0" />
            <div className="flex flex-1 items-center gap-2">
              <input
                type="date"
                className="w-1/2 p-1.5 text-xs border rounded text-gray-600"
                value={dateFilter.from}
                onChange={e => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
              />
              <span className="text-gray-400">-</span>
              <input
                type="date"
                className="w-1/2 p-1.5 text-xs border rounded text-gray-600"
                value={dateFilter.to}
                onChange={e => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 max-w-6xl mx-auto">
        {filteredTickets.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
            <SafeIcon icon={FiMessageSquare} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">ยังไม่มีรายการ</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
            {filteredTickets.map(ticket => (
              <div
                key={ticket.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 flex flex-col h-full hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex justify-between items-start gap-3 mb-3">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg leading-snug break-words min-w-0">
                    {ticket.topic}
                  </h3>
                  <div className="flex-shrink-0">{getStatusBadge(ticket.status)}</div>
                </div>

                <div className="text-sm text-gray-600 space-y-2 mb-3 flex-1">
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5">
                    <span className="text-gray-400">ผู้รับผิดชอบ:</span>
                    <span className="font-medium text-gray-800 break-words">{ticket.assignee?.displayName || ticket.assignee?.lineUserId || 'Unknown'}</span>
                    <span className="text-gray-400">ผู้สร้าง:</span>
                    <span className="text-gray-700 break-words">{ticket.createdBy?.displayName || ticket.createdBy?.lineUserId || 'Unknown'}</span>
                    <span className="text-gray-400">วันที่สร้าง:</span>
                    <span className="text-gray-700">{new Date(ticket.createdAt).toLocaleDateString('th-TH')}</span>
                  </div>
                  {ticket.comments && ticket.comments.length > 0 && (
                    <div className="bg-gray-50 p-3 rounded-lg text-sm mt-2 border border-gray-100 max-h-44 overflow-y-auto">
                      <span className="text-gray-500 block mb-2 text-xs font-semibold uppercase tracking-wide">ประวัติรายการ</span>
                      <ul className="space-y-2">
                        {ticket.comments.map((c, idx) => (
                          <li key={idx} className="text-xs pb-2 border-b border-gray-200 last:border-0 last:pb-0">
                            <div className="flex flex-wrap items-baseline gap-x-2">
                              <span className="font-medium text-gray-700">{c.userName}</span>
                              <span className="text-gray-400">
                                {new Date(c.createdAt).toLocaleString('th-TH', {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <p className={`mt-1 break-words ${c.type === 'status_change' ? 'text-indigo-600 font-medium' : 'text-gray-600'}`}>
                              {c.text}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {ticket.status !== 'closed' && (
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 mt-auto">
                    <button
                      onClick={() => setCommentModal({ show: true, ticketId: ticket.id, comment: '' })}
                      className="flex items-center justify-center text-xs px-2 py-2 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                    >
                      <SafeIcon icon={FiMessageSquare} className="w-3.5 h-3.5 mr-1 flex-shrink-0" /> คอมเมนต์
                    </button>
                    <button
                      onClick={() => handleNotifyAgain(ticket.id)}
                      className="flex items-center justify-center text-xs px-2 py-2 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-200 hover:bg-yellow-100 transition-colors"
                    >
                      <SafeIcon icon={FiAlertCircle} className="w-3.5 h-3.5 mr-1 flex-shrink-0" /> แจ้งซ้ำ
                    </button>
                    <button
                      onClick={() => setStatusModal({ show: true, ticketId: ticket.id, status: ticket.status })}
                      className="flex items-center justify-center text-xs px-2 py-2 bg-gray-50 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 mr-1 flex-shrink-0" /> สถานะ
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {statusModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">เปลี่ยนสถานะ</h2>
              <button onClick={() => setStatusModal({ show: false, ticketId: null, status: '' })} className="text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleStatusChange} className="p-4 space-y-4">
              <div>
                <select
                  value={statusModal.status}
                  onChange={(e) => setStatusModal(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  required
                >
                  <option value="created">สร้างใหม่ (Created)</option>
                  <option value="notify">แจ้งเตือน (Notify)</option>
                  <option value="pending">รอดำเนินการ (Pending)</option>
                  <option value="closed">ปิดรายการ (Closed)</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setStatusModal({ show: false, ticketId: null, status: '' })}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submittingStatus}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium flex items-center"
                >
                  {submittingStatus && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {commentModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">เพิ่มคอมเมนต์</h2>
              <button onClick={() => setCommentModal({ show: false, ticketId: null, comment: '' })} className="text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddComment} className="p-4 space-y-4">
              <div>
                <textarea
                  value={commentModal.comment}
                  onChange={(e) => setCommentModal(prev => ({ ...prev, comment: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="พิมพ์ข้อความที่นี่..."
                  required
                ></textarea>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setCommentModal({ show: false, ticketId: null, comment: '' })}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submittingComment}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium flex items-center"
                >
                  {submittingComment && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">สร้างรายการใหม่</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">หัวข้อ (Topic) *</label>
                <input
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="เช่น ขออนุมัติเอกสาร..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ผู้รับผิดชอบ (Assignee) *</label>
                <select
                  name="assigneeId"
                  value={formData.assigneeId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">-- เลือกผู้รับผิดชอบ --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.displayName || u.lineUserId} ({u.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">รายละเอียดเพิ่มเติม</label>
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="พิมพ์รายละเอียดที่นี่..."
                ></textarea>
              </div>

              <div className="pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium flex items-center"
                >
                  {submitting && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineTickets;
